import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Edit, Trash2, Eye, Filter, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InvoiceHistory() {
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Error fetching invoices');
    }
  };

  const handleDelete = async (invoiceId) => {
    try {
      await axios.delete(`${API}/invoices/${invoiceId}`);
      toast.success('Invoice deleted successfully');
      fetchInvoices();
      setShowDeleteDialog(false);
      setInvoiceToDelete(null);
    } catch (error) {
      toast.error('Error deleting invoice');
      console.error(error);
    }
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewDialog(true);
  };

  const downloadInvoicePDF = (invoice) => {
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      let yPos = 20;

      // Header
      pdf.setFontSize(24);
      pdf.setTextColor(127, 161, 195);
      pdf.text('Convella', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 8;
      pdf.setFontSize(12);
      pdf.setTextColor(107, 114, 128);
      pdf.text('The Taste of Real Ice Cream', pageWidth / 2, yPos, { align: 'center' });
      
      yPos += 15;
      pdf.setDrawColor(229, 231, 235);
      pdf.line(20, yPos, pageWidth - 20, yPos);
      
      yPos += 10;
      pdf.setFontSize(11);
      pdf.setTextColor(31, 41, 55);
      
      // Invoice details
      pdf.text(`Vendor: ${invoice.vendor_name}`, 20, yPos);
      yPos += 7;
      pdf.text(`Date: ${invoice.date}`, 20, yPos);
      yPos += 7;
      pdf.text(`Amount Submitted: ₹${invoice.amount_submitted.toFixed(2)}`, 20, yPos);
      
      yPos += 10;
      pdf.line(20, yPos, pageWidth - 20, yPos);
      yPos += 10;
      
      // Financial details
      pdf.setFontSize(10);
      pdf.text('Total Sales:', 20, yPos);
      pdf.setTextColor(127, 161, 195);
      pdf.text(`₹${invoice.total_sales.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
      
      yPos += 7;
      pdf.setTextColor(31, 41, 55);
      pdf.text('Vendor Commission:', 20, yPos);
      pdf.setTextColor(245, 158, 11);
      pdf.text(`₹${invoice.vendor_commission.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
      
      yPos += 7;
      pdf.setTextColor(31, 41, 55);
      pdf.text('Factory Share:', 20, yPos);
      pdf.setTextColor(144, 210, 109);
      pdf.text(`₹${invoice.factory_share.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
      
      yPos += 7;
      pdf.setTextColor(31, 41, 55);
      pdf.text('Stock Limit:', 20, yPos);
      pdf.text(`₹${invoice.stock_limit.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
      
      yPos += 10;
      pdf.line(20, yPos, pageWidth - 20, yPos);
      yPos += 10;
      
      // Items
      if (invoice.items && invoice.items.length > 0) {
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'bold');
        pdf.text('Items:', 20, yPos);
        pdf.setFont(undefined, 'normal');
        yPos += 7;
        
        pdf.setFontSize(9);
        invoice.items.forEach((item) => {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          const flavourText = item.flavour ? ` (${item.flavour})` : '';
          pdf.text(`${item.item_name}${flavourText} (₹${item.price}) x ${item.quantity}`, 25, yPos);
          pdf.text(`₹${item.total}`, pageWidth - 20, yPos, { align: 'right' });
          yPos += 6;
        });
      }

      // Bazaar Items
      if (invoice.bazaar_items && invoice.bazaar_items.length > 0) {
        yPos += 5;
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(194, 120, 3);
        pdf.text('Bazaar Items (Not in Invoice):', 20, yPos);
        pdf.setFont(undefined, 'normal');
        yPos += 7;

        pdf.setFontSize(9);
        let bazaarTotal = 0;
        invoice.bazaar_items.forEach((item) => {
          if (yPos > 270) {
            pdf.addPage();
            yPos = 20;
          }
          pdf.text(`${item.item_name} (₹${item.price}) x ${item.quantity}`, 25, yPos);
          pdf.text(`₹${item.total}`, pageWidth - 20, yPos, { align: 'right' });
          bazaarTotal += item.total;
          yPos += 6;
        });
        yPos += 2;
        pdf.setFontSize(9);
        pdf.text(`Bazaar Total: ₹${bazaarTotal}`, pageWidth - 20, yPos, { align: 'right' });
        pdf.setTextColor(31, 41, 55);
      }
      
      yPos += 5;
      pdf.line(20, yPos, pageWidth - 20, yPos);
      yPos += 8;
      
      // Totals
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'bold');
      pdf.text('Total Stock Value:', 20, yPos);
      pdf.text(`₹${invoice.total_stock_value.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
      
      yPos += 7;
      const balanceColor = invoice.balance < 0 ? [220, 38, 38] : [22, 163, 74];
      pdf.setTextColor(...balanceColor);
      pdf.text('Remaining Balance:', 20, yPos);
      pdf.text(`₹${invoice.balance.toFixed(2)}`, pageWidth - 20, yPos, { align: 'right' });
      
      pdf.save(`invoice-${invoice.vendor_name}-${invoice.date}.pdf`);
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const filteredInvoices = invoices.filter(
    (invoice) =>
      invoice.vendor_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.date.includes(searchTerm)
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold text-[#7FA1C3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Invoice History
          </h1>
          <p className="text-slate-600 mt-2">View and manage all invoices</p>
        </div>

        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by vendor or date..."
            data-testid="search-invoices-input"
            className="pl-10 rounded-full"
          />
        </div>
      </div>

      <Card className="shadow-sm border-slate-200">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <CardTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
            All Invoices ({filteredInvoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {filteredInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="invoices-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-sm">Vendor</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Date</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Total Sales</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Stock Value</th>
                    <th className="text-center py-3 px-4 font-semibold text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                      data-testid={`invoice-row-${invoice.id}`}
                    >
                      <td className="py-3 px-4 text-sm font-medium">{invoice.vendor_name}</td>
                      <td className="py-3 px-4 text-sm">{invoice.date}</td>
                      <td className="py-3 px-4 text-sm text-right">₹{invoice.amount_submitted.toFixed(2)}</td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-[#7FA1C3]">
                        ₹{invoice.total_sales.toFixed(2)}
                      </td>
                      <td className="py-3 px-4 text-sm text-right">₹{invoice.total_stock_value.toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            onClick={() => handleView(invoice)}
                            data-testid={`view-invoice-${invoice.id}`}
                            className="p-2 h-auto bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full"
                            title="View invoice"
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            onClick={() => downloadInvoicePDF(invoice)}
                            data-testid={`download-invoice-${invoice.id}`}
                            className="p-2 h-auto bg-[#90D26D] hover:bg-[#7AB856] text-white rounded-full"
                            title="Download PDF"
                          >
                            <Download size={16} />
                          </Button>
                          <Button
                            onClick={() => {
                              setInvoiceToDelete(invoice);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-invoice-${invoice.id}`}
                            className="p-2 h-auto bg-red-500 hover:bg-red-600 text-white rounded-full"
                            title="Delete invoice"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <p>No invoices found</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="view-invoice-dialog">
          {selectedInvoice && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Invoice Details
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="text-center pb-4 border-b">
                  <h2 className="text-2xl font-extrabold text-[#7FA1C3]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Convella
                  </h2>
                  <p className="text-sm text-slate-600">The Taste of Real Ice Cream</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-slate-600">Vendor:</span>
                    <p className="font-medium">{selectedInvoice.vendor_name}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-600">Date:</span>
                    <p className="font-medium">{selectedInvoice.date}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-600">Amount Submitted:</span>
                    <p className="font-medium">₹{selectedInvoice.amount_submitted.toFixed(2)}</p>
                  </div>
                  <div>
                    <span className="font-semibold text-slate-600">Commission Type:</span>
                    <p className="font-medium">
                      {selectedInvoice.commission_type === 'taken'
                        ? 'Vendor has taken commission'
                        : 'Vendor has NOT taken commission'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 py-4 border-y">
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Total Sales:</span>
                    <span className="font-bold text-[#7FA1C3]">₹{selectedInvoice.total_sales.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Vendor Commission:</span>
                    <span className="text-[#F59E0B]">₹{selectedInvoice.vendor_commission.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Factory Share:</span>
                    <span className="text-[#90D26D]">₹{selectedInvoice.factory_share.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Stock Limit:</span>
                    <span>₹{selectedInvoice.stock_limit.toFixed(2)}</span>
                  </div>
                </div>

                {selectedInvoice.items && selectedInvoice.items.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm">Items:</h3>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {selectedInvoice.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs bg-slate-50 p-2 rounded">
                          <span>
                            {item.item_name}{item.flavour ? ` (${item.flavour})` : ''} x {item.quantity} (₹{item.price})
                          </span>
                          <span className="font-semibold">₹{item.total}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedInvoice.bazaar_items && selectedInvoice.bazaar_items.length > 0 && (
                  <div className="space-y-2 pt-3 mt-1 border-t border-dashed border-orange-300">
                    <h3 className="font-semibold text-sm text-orange-600">Bazaar Items (Not in Invoice)</h3>
                    <div className="space-y-1">
                      {selectedInvoice.bazaar_items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-xs bg-orange-50 p-2 rounded">
                          <span>{item.item_name} x {item.quantity} (₹{item.price})</span>
                          <span className="font-semibold text-orange-600">₹{item.total}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-right text-xs text-orange-600 font-semibold">
                      Bazaar Total: ₹{selectedInvoice.bazaar_items.reduce((sum, item) => sum + item.total, 0)}
                    </div>
                  </div>
                )}

                {selectedInvoice.upi_amount > 0 && (
                  <div className="grid grid-cols-2 gap-4 text-sm pt-2 border-t">
                    <div>
                      <span className="font-semibold text-purple-600">UPI/Paytm:</span>
                      <p className="font-medium">₹{selectedInvoice.upi_amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <span className="font-semibold text-green-600">Cash:</span>
                      <p className="font-medium">₹{(selectedInvoice.cash_amount || 0).toFixed(2)}</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2 pt-4 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">Total Stock Value:</span>
                    <span className="font-bold">₹{selectedInvoice.total_stock_value.toFixed(2)}</span>
                  </div>
                  <div
                    className={`flex justify-between text-sm font-bold ${
                      selectedInvoice.balance < 0 ? 'text-red-600' : 'text-green-600'
                    }`}
                  >
                    <span>Remaining Balance:</span>
                    <span>₹{selectedInvoice.balance.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => setShowViewDialog(false)}
                  className="bg-[#7FA1C3] hover:bg-[#6482A6] text-white rounded-full"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete the invoice for{' '}
              <span className="font-bold">{invoiceToDelete?.vendor_name}</span> dated{' '}
              <span className="font-bold">{invoiceToDelete?.date}</span>?
            </p>
            <p className="text-sm text-red-600 mt-2">This action cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowDeleteDialog(false);
                setInvoiceToDelete(null);
              }}
              className="bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDelete(invoiceToDelete.id)}
              data-testid="confirm-delete-invoice"
              className="bg-red-500 hover:bg-red-600 text-white rounded-full"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
